// Conversational SEO — client transport for the POST-based SSE chat stream.
// EventSource can't POST (and we must POST a growing transcript), so we read the response
// body as a stream and parse SSE frames (`data: {json}\n\n`) ourselves. ": heartbeat" comment
// lines are ignored.

// Visible transcript entries use a simple {role, content:string}. Across a client-tool pause the
// server hands back raw Anthropic message blocks (with tool_use/tool_result), which we pass back
// verbatim on resume — hence the broader `unknown` message type on the wire.
export interface ChatMessage { role: "user" | "assistant"; content: string }
export type WireMessage = ChatMessage | { role: string; content: unknown };

export interface ClientToolCall { id: string; name: string; input: Record<string, unknown> }

export interface ChatEvent {
  type: "text" | "tool_call" | "tool_result" | "awaiting_client_tools" | "done" | "error";
  delta?: string;      // text
  id?: string;         // tool_call / tool_result
  name?: string;       // tool_call / tool_result
  ok?: boolean;        // tool_result
  error?: string;      // tool_result
  message?: string;    // error
  // awaiting_client_tools:
  messages?: unknown[];                 // accumulated transcript incl. the pausing assistant turn
  serverResults?: unknown[];            // tool_result blocks already computed server-side this turn
  clientTools?: ClientToolCall[];       // client-side (WordPress) tools to execute
}

export async function streamChat(
  body: { siteId: string; messages: WireMessage[] },
  onEvent: (e: ChatEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch("/api/chat-stream", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return;
    onEvent({ type: "error", message: e instanceof Error ? e.message : "Network error" });
    return;
  }

  if (!res.ok || !res.body) {
    let msg = `Request failed (${res.status})`;
    try { const j = await res.json() as { error?: string }; if (j.error) msg = j.error; } catch { /* ignore */ }
    onEvent({ type: "error", message: msg });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        for (const line of frame.split("\n")) {
          const t = line.trimStart();
          if (!t.startsWith("data:")) continue; // skips ": heartbeat" comments
          const json = t.slice(5).trim();
          if (!json) continue;
          try { onEvent(JSON.parse(json) as ChatEvent); } catch { /* ignore malformed frame */ }
        }
      }
    }
  } catch (e) {
    if ((e as { name?: string })?.name !== "AbortError") {
      onEvent({ type: "error", message: e instanceof Error ? e.message : "Stream error" });
    }
  }
}
